import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Server, ChevronDown, Users, Home, CreditCard, LayoutDashboard } from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';
import { ThemeToggle } from './ThemeToggle';
import { Logo } from './Logo';

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Desktop Floating Pill Navbar */}
      <header 
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 pointer-events-none",
          isScrolled ? "pt-2 md:pt-4" : "pt-4 md:pt-6"
        )}
      >
        <div className={cn(
          "mx-auto transition-all duration-300 pointer-events-auto",
          isScrolled ? "max-w-4xl px-4" : "max-w-6xl px-4 sm:px-6 lg:px-8"
        )}>
          <div className={cn(
            "flex items-center justify-between transition-all duration-300 rounded-2xl md:rounded-full px-4 md:px-6",
            isScrolled 
              ? "py-3 bg-olo-dark/80 backdrop-blur-lg border border-olo-border shadow-[0_8px_32px_rgba(0,0,0,0.4)]" 
              : "py-4 md:py-5 bg-transparent border border-transparent"
          )}
          style={{ transform: 'translateZ(0)' }}
          >
            
            {/* Logo */}
            <Link to="/" className="flex items-center z-50 py-4 md:py-0 group">
              <Logo className="h-9 w-auto text-white group-hover:opacity-90 transition-opacity" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              <Link 
                to="/infrastructure" 
                className="text-sm font-medium text-gray-300 hover:text-white transition-colors py-2"
              >
                How it works
              </Link>
              
              {/* Solutions Dropdown */}
              <div className="relative" onMouseEnter={() => setIsSolutionsOpen(true)} onMouseLeave={() => setIsSolutionsOpen(false)}>
                <button className="flex items-center gap-1 text-sm font-medium text-gray-300 hover:text-white transition-colors py-2">
                  Services <ChevronDown className="w-4 h-4" />
                </button>
                <AnimatePresence>
                  {isSolutionsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 glass-panel rounded-2xl overflow-hidden shadow-2xl"
                    >
                      <div className="p-2">
                        <Link to="/agencies" className="flex items-start gap-3 p-3 rounded-xl hover:bg-olo-border/50 transition-colors">
                          <Users className="w-5 h-5 text-olo-green mt-0.5" />
                          <div>
                            <div className="font-medium text-white text-sm">For Agency Owners</div>
                            <div className="text-xs text-gray-400 mt-0.5">Partner-Label hosting</div>
                          </div>
                        </Link>
                        <Link to="/owners" className="flex items-start gap-3 p-3 rounded-xl hover:bg-olo-border/50 transition-colors">
                          <Server className="w-5 h-5 text-olo-teal mt-0.5" />
                          <div>
                            <div className="font-medium text-white text-sm">For Website Owners</div>
                            <div className="text-xs text-gray-400 mt-0.5">Premium managed WP</div>
                          </div>
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link 
                to="/pricing" 
                className="text-sm font-medium text-gray-300 hover:text-white transition-colors py-2"
              >
                Pricing
              </Link>
              <Link 
                to="/tools" 
                className="text-sm font-medium text-gray-300 hover:text-white transition-colors py-2"
              >
                Tools
              </Link>
              <Link 
                to="/contact" 
                className="text-sm font-medium text-gray-300 hover:text-white transition-colors py-2"
              >
                Contact
              </Link>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <ThemeToggle />
              <a 
                href="https://os.olokonnect.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-olo-dark text-sm font-bold rounded-full hover:opacity-90 transition-opacity"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </a>
            </div>

            {/* Mobile Menu Button (Only shows if needed, but we use bottom nav mostly) */}
            <div className="md:hidden flex items-center gap-2">
              <ThemeToggle />
              <button 
                className="p-2 text-gray-300 hover:text-white"
                onClick={() => setIsOpen(!isOpen)}
                aria-label={isOpen ? "Close menu" : "Open menu"}
                aria-expanded={isOpen}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Top Menu (For secondary actions like Book a Call) */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-olo-card border-b border-olo-border overflow-hidden pointer-events-auto"
            >
              <div className="px-4 py-6 space-y-4">
                <a 
                  href="https://os.olokonnect.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-white text-olo-dark font-bold rounded-xl hover:opacity-90 transition-opacity"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  Dashboard
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Mobile Bottom Navigation App-Style */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass-panel border-t border-olo-border pb-safe">
        <div className="flex justify-around items-center h-16 px-2">
          <Link to="/" className={cn("flex flex-col items-center justify-center w-full h-full space-y-1", location.pathname === '/' ? "text-olo-green" : "text-gray-400")}>
            <Home className="w-5 h-5" />
            <span className="text-[10px] font-medium">Home</span>
          </Link>
          <Link to="/agencies" className={cn("flex flex-col items-center justify-center w-full h-full space-y-1", location.pathname === '/agencies' ? "text-olo-green" : "text-gray-400")}>
            <Users className="w-5 h-5" />
            <span className="text-[10px] font-medium">Agencies</span>
          </Link>
          <Link to="/owners" className={cn("flex flex-col items-center justify-center w-full h-full space-y-1", location.pathname === '/owners' ? "text-olo-green" : "text-gray-400")}>
            <Server className="w-5 h-5" />
            <span className="text-[10px] font-medium">Owners</span>
          </Link>
          <Link to="/pricing" className={cn("flex flex-col items-center justify-center w-full h-full space-y-1", location.pathname === '/pricing' ? "text-olo-green" : "text-gray-400")}>
            <CreditCard className="w-5 h-5" />
            <span className="text-[10px] font-medium">Pricing</span>
          </Link>
        </div>
      </div>
    </>
  );
}
